window.mixmatchyPhotoColors = function () {
  "use strict";

  if (!window.theme || !theme.Variants || !theme.Product) return;

  function colorPicker(container) {
    return container.querySelector("[data-photo-color-picker]");
  }

  // Keep exact selections, including sold-out combinations. Never substitute a size.
  const getVariant = theme.Variants.prototype._getVariantFromOptions;
  theme.Variants.prototype._getVariantFromOptions = function (
    lastSelectedOption,
  ) {
    return colorPicker(this.container)
      ? this._getFullMatch(false) || null
      : getVariant.call(this, lastSelectedOption);
  };

  const updateHistory = theme.Variants.prototype._updateHistoryState;
  theme.Variants.prototype._updateHistoryState = function (variant) {
    if (!colorPicker(this.container)) return updateHistory.call(this, variant);
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variant.id);
    window.history.replaceState({ path: url.href }, "", url.href);
  };

  // Native availability is hierarchical: a color (option1) shows sold out only
  // when every size is. Check each value against the shopper's other choices so
  // picking a size shows which colors are in stock in that size.
  const availableOptions =
    theme.VariantAvailability?.prototype.createAvailableOptionsTree;
  if (availableOptions) {
    const prototype = theme.VariantAvailability.prototype;
    prototype.createAvailableOptionsTree = function (variants, selected) {
      if (!colorPicker(this.container)) {
        return availableOptions.apply(this, arguments);
      }
      const options = { option1: [], option2: [], option3: [] };
      variants.forEach((variant) => {
        Object.keys(options).forEach((index) => {
          if (variant[index] === null) return;
          let entry = options[index].find(
            (option) => option.value === variant[index],
          );
          if (!entry) {
            entry = { value: variant[index], soldOut: true };
            options[index].push(entry);
          }
          const matchesOthers = selected.every(
            (option) =>
              option.index === index ||
              variant[option.index] === option.value,
          );
          if (variant.available && matchesOthers) entry.soldOut = false;
        });
      });
      return options;
    };
  }

  const setupVariants = theme.Product.prototype.initVariants;
  theme.Product.prototype.initVariants = function () {
    setupVariants.call(this);
    const picker = colorPicker(this.container);
    if (!picker || !this.variants) return;
    this.container.on("variantChange.photoColors", (event) => {
      this.updatePhotoSelection(event.detail.variant);
    });
    this.updatePhotoSelection(this.variants.currentVariant);
    // A checked radio doesn't emit change when clicked again. Return to its
    // assigned main image even after the shopper has browsed other gallery photos.
    this._photoColorClickAbort?.abort();
    this._photoColorClickAbort = new AbortController();
    picker.addEventListener(
      "click",
      (event) => {
        const label = event.target.closest("label.photo-color-tile");
        if (!label?.control?.checked) return;
        this.showPhotoColor(label.control.value);
      },
      { signal: this._photoColorClickAbort.signal },
    );
  };

  const variantImage = theme.Product.prototype.updateVariantImage;
  theme.Product.prototype.updateVariantImage = function (event) {
    if (!colorPicker(this.container)) return variantImage.call(this, event);
    if (this._photoGallerySelecting) return;
    if (this.settings.imageSetName) return;
    const gallery = this.flickity?.slideshow;
    const mediaId = event.detail.variant?.featured_media?.id;
    if (!gallery || !mediaId) return;
    const index = gallery.cells.findIndex(
      (cell) => cell.element.dataset.photoMediaId === String(mediaId),
    );
    if (index >= 0) gallery.select(index, false, true);
  };

  // Zoom's original media indexes differ from the mapped gallery when orphan
  // photos are excluded. The 9.2 carousel accepts stable media IDs directly.
  const zoomChange = theme.Photoswipe.prototype.afterChange;
  theme.Photoswipe.prototype.afterChange = function () {
    const product = this.container.closest(".product-section");
    const slide = this.items[this.gallery.getCurrentIndex()]?.el?.closest(
      ".product-main-slide",
    );
    if (!product || !colorPicker(product) || !slide?.dataset.photoMediaId) {
      return zoomChange.call(this);
    }
    this.container.dispatchEvent(
      new CustomEvent("photoswipe:afterChange", {
        detail: { index: slide.dataset.photoMediaId },
      }),
    );
  };

  // Bind each rebuilt strip once and select its images by identity. Old
  // carousel listeners must not continue selecting images after a color change.
  const childNavigation = theme.Slideshow.prototype.initChildNav;
  theme.Slideshow.prototype.initChildNav = function () {
    const product = this.el.closest(".product-section");
    if (!product || !colorPicker(product)) return childNavigation.call(this);
    const nav = this.args.childNav;
    nav.photoColorNavigationController?.abort();
    this._photoNavAbort = new AbortController();
    nav.photoColorNavigationController = this._photoNavAbort;
    const options = { signal: this._photoNavAbort.signal };
    this.childNavLinks[this.args.initialIndex]?.classList.add("is-active");
    this.childNavLinks.forEach((link, index) => {
      link.dataset.index = index;
      const selectImage = () => {
        const position = this.slideshow.cells.findIndex(
          (cell) => cell.element.dataset.photoMediaId === link.dataset.id,
        );
        if (position >= 0) {
          // Browsing the strip must leave its position under the shopper's
          // control, including the carousel's later settle callback.
          this._photoPinnedThumbId = link.dataset.id;
          this.slideshow.select(position, false, true);
        }
      };
      link.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          selectImage();
        },
        options,
      );
      link.addEventListener("focus", selectImage, options);
      link.addEventListener(
        "keydown",
        (event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          selectImage();
        },
        options,
      );
    });
    this.arrows.forEach((arrow) => {
      arrow.addEventListener("click", this.arrowClick.bind(this), options);
    });
    // The native constructor binds navigation before creating its carousel.
    // Scroll once the selected cell and thumbnail viewport have their sizes.
    requestAnimationFrame(() => {
      if (!this.slideshow?.isActive) return;
      let previousWidth = this.el.getBoundingClientRect().width;
      if (previousWidth > 0) this.resize();
      // Follow the gallery's actual width when the responsive layout changes.
      if (!window.ResizeObserver) return;
      this._photoResizeObserver = new ResizeObserver(() => {
        const width = this.el.getBoundingClientRect().width;
        if (width <= 0 || Math.abs(width - previousWidth) < 0.5) return;
        previousWidth = width;
        requestAnimationFrame(() => {
          if (this.slideshow?.isActive) this.resize();
        });
      });
      this._photoResizeObserver.observe(this.el);
    });
  };

  const childGoTo = theme.Slideshow.prototype.childNavGoTo;
  theme.Slideshow.prototype.childNavGoTo = function (index) {
    const product = this.el.closest(".product-section");
    if (!product || !colorPicker(product)) return childGoTo.call(this, index);
    const mediaId =
      this.slideshow?.cells[index]?.element.dataset.photoMediaId ||
      this.el.querySelector(".flickity-slider .is-selected")?.dataset
        .photoMediaId;
    const link = Array.from(this.childNavLinks).find(
      (item) => item.dataset.id === mediaId,
    );
    this.childNavLinks.forEach((item) => {
      item.blur();
      item.classList.toggle("is-active", item === link);
    });
    const scroller = this.args.childNavScroller;
    if (!link || !scroller) return;
    if (mediaId === this._photoPinnedThumbId) return;
    if (
      this.args.childVertical &&
      window.matchMedia("(min-width: 769px)").matches
    ) {
      scroller.scrollTop = link.offsetTop - 100;
    } else {
      scroller.scrollLeft = link.offsetLeft - 100;
    }
  };

  const resizeGallery = theme.Slideshow.prototype.resize;
  theme.Slideshow.prototype.resize = function () {
    const photoGallery = this.el.closest("[data-photo-color-gallery]");
    if (
      photoGallery &&
      (this.slideshow.isAnimating || this.slideshow.isDragging)
    ) {
      // Flickity skips resizing during movement. Retry after it settles so a
      // responsive width change cannot leave the image at its old position.
      this._photoResizePending = true;
      return;
    }
    this._photoResizePending = false;
    resizeGallery.call(this);
    if (photoGallery && this.slideshow.isActive) {
      this.childNavGoTo(this.slideshow.selectedIndex);
    }
  };

  const settleGallery = theme.Slideshow.prototype.afterChange;
  theme.Slideshow.prototype.afterChange = function (index) {
    settleGallery.call(this, index);
    if (this._photoResizePending) this.resize();
  };

  const destroyGallery = theme.Slideshow.prototype.destroy;
  theme.Slideshow.prototype.destroy = function () {
    this._photoNavAbort?.abort();
    this._photoResizeObserver?.disconnect();
    return destroyGallery.call(this);
  };

  theme.Product.prototype.updatePhotoSelection = function (variant) {
    const selectedColor = colorPicker(this.container).querySelector(
      "input:checked",
    );
    if (selectedColor)
      this.updateColorName(
        selectedColor.value,
        selectedColor.dataset.colorIndex,
      );
    const unavailable = !variant || !variant.available;
    this.container.toggleAttribute("data-photo-size-unavailable", unavailable);
    const master = this.container.querySelector(
      this.selectors.originalSelectorId,
    );
    if (unavailable && master) {
      master.selectedIndex = -1;
      master.dispatchEvent(new Event("change", { bubbles: true }));
      if (!variant && this.settings.enableHistoryState) {
        const url = new URL(window.location.href);
        url.searchParams.delete("variant");
        window.history.replaceState({ path: url.href }, "", url.href);
      }
    }
    if (!variant) {
      const buttonText = this.container.querySelector(
        this.selectors.addToCartText,
      );
      const picker = colorPicker(this.container);
      const color = picker.querySelector("input:checked")?.value;
      const hasAvailableSize = this.variantsObject.some(
        (item) => item[picker.dataset.index] === color && item.available,
      );
      if (buttonText) {
        buttonText.textContent = hasAvailableSize
          ? "Choose an available size"
          : theme.strings.soldOut;
      }
    }
    // Keep accessibility state aligned with the theme's availability classes.
    this.container.querySelectorAll("[data-variant-input]").forEach((input) => {
      const value = input.dataset.colorName || input.value;
      input.setAttribute(
        "aria-label",
        value + (input.classList.contains("disabled") ? ", sold out" : ""),
      );
    });
  };

  // Keep the gallery in source order across every offered color. Unassigned
  // photos are included only when explicitly marked as shared (e.g. size charts).
  const imageArguments = theme.Product.prototype.imageSetArguments;
  theme.Product.prototype.imageSetArguments = function (variant) {
    const picker = colorPicker(this.container);
    if (!picker) return imageArguments.call(this, variant);
    this.cache.mainSlider
      .closest("[data-product-images]")
      .setAttribute("data-photo-color-gallery", "");
    const color = picker.querySelector("input:checked")?.value;
    const imageVariant = this.variantsObject.find(
      (item) => item[picker.dataset.index] === color && item.featured_media,
    );
    const cellSelector =
      '.product-main-slide[data-photo-color], .product-main-slide[data-photo-shared="true"]';
    const slides = Array.from(
      this.cache.mainSlider.querySelectorAll(cellSelector),
    );
    const mainIndex = imageVariant
      ? slides.findIndex(
          (slide) =>
            slide.dataset.photoMediaId ===
            String(imageVariant.featured_media.id),
        )
      : slides.findIndex((slide) => slide.dataset.photoColor === color);
    this.container.toggleAttribute("data-photo-gallery-empty", mainIndex < 0);
    this.settings.currentImageSet = this.getImageSetName(color);
    this.settings.currentSlideIndex = mainIndex < 0 ? 0 : mainIndex;
    return {
      // If no media can be mapped, keep the native carousel initialized but
      // hidden rather than presenting a different color as the selected one.
      cellSelector: slides.length ? cellSelector : ".product-main-slide",
      imageSet:
        this.settings.imageSetName + "_" + this.settings.currentImageSet,
      initialIndex: this.settings.currentSlideIndex,
    };
  };

  theme.Product.prototype.showPhotoColor = function (color) {
    if (this._photoGallerySelecting) return;
    const picker = colorPicker(this.container);
    const gallery = this.flickity?.slideshow;
    if (!picker || !gallery) return;
    this.flickity._photoPinnedThumbId = null;
    const variant = this.variantsObject.find(
      (item) => item[picker.dataset.index] === color && item.featured_media,
    );
    let index = variant
      ? gallery.cells.findIndex(
          (cell) =>
            cell.element.dataset.photoMediaId ===
            String(variant.featured_media.id),
        )
      : -1;
    if (index < 0) {
      index = gallery.cells.findIndex(
        (cell) => cell.element.dataset.photoColor === color,
      );
    }
    if (this.settings.imageSetName) {
      this.container.toggleAttribute("data-photo-gallery-empty", index < 0);
    }
    if (index >= 0) gallery.select(index, false, true);
  };

  // Flickity moves filtered cells. Restore source order before rebuilding so
  // image indexes keep matching the thumbnail links after repeated color changes.
  const initSlider = theme.Product.prototype.initProductSlider;
  theme.Product.prototype.initProductSlider = function (variant) {
    if (colorPicker(this.container) && this.settings.imageSetName) {
      if (this.flickity) {
        this.flickity.destroy();
        this.flickity = null;
      }
      Array.from(this.cache.mainSlider.querySelectorAll(".product-main-slide"))
        .sort((a, b) => Number(a.dataset.index) - Number(b.dataset.index))
        .forEach((slide) => this.cache.mainSlider.appendChild(slide));
    }
    return initSlider.call(this, variant);
  };

  const imageThumbs = theme.Product.prototype.updateImageSetThumbs;
  theme.Product.prototype.updateImageSetThumbs = function (set) {
    if (!colorPicker(this.container)) return imageThumbs.call(this, set);
    this.cache.thumbSlider
      .querySelectorAll(".product__thumb-item")
      .forEach((thumb) => {
        thumb.classList.toggle(
          "hide",
          !thumb.dataset.photoColor && thumb.dataset.photoShared !== "true",
        );
      });
  };

  // Changing color snaps to its main photo without rebuilding the gallery.
  const updateImages = theme.Product.prototype.updateImageSet;
  theme.Product.prototype.updateImageSet = function (event) {
    const picker = colorPicker(this.container);
    if (!picker || !this.settings.imageSetIndex)
      return updateImages.call(this, event);
    const color = picker.querySelector("input:checked")?.value;
    if (!color) return;
    const setValue = this.getImageSetName(color);
    if (this.settings.currentImageSet === setValue) return;
    this.settings.currentImageSet = setValue;
    this.showPhotoColor(color);
  };

  // Browsing a photo selects its color while preserving that particular view
  // and the shopper's size. Shared media does not change the selected color.
  const slideChange = theme.Product.prototype.onSlideChange;
  theme.Product.prototype.onSlideChange = function (index) {
    const picker = colorPicker(this.container);
    if (!picker || !this.settings.imageSetName) {
      return slideChange.call(this, index);
    }
    const next = this.cache.mainSlider.querySelector(
      ".flickity-slider .is-selected",
    );
    if (!next) return;
    if (this._photoActiveSlide && this._photoActiveSlide !== next) {
      this._photoActiveSlide.setAttribute("tabindex", "-1");
      this.stopMediaOnSlide(this._photoActiveSlide);
    }
    next.setAttribute("tabindex", "0");
    this.prepMediaOnSlide(next);
    this._photoActiveSlide = next;
    this.settings.currentSlideIndex = index;
    const input = Array.from(picker.querySelectorAll("input")).find(
      (item) => item.value === next.dataset.photoColor,
    );
    if (!input) return;
    this.container.removeAttribute("data-photo-gallery-empty");
    if (input.checked) return;
    this._photoGallerySelecting = true;
    try {
      input.checked = true;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    } finally {
      this._photoGallerySelecting = false;
    }
  };
};
